// Survey-answer identity + activity-delta helpers.
//
// Answers identify by `code`, falling back to surveyQuestionId for legacy records
// written before SurveyAnswer.code existed. The activity-delta removal check and the
// answer→question lookup both rely on that rule, so they live together here.
//
// The daily-summary email counts the same delta and has its own implementation of this partition in
// server/app/lib/App/Utils/Activity/SurveyAnswerDelta.php. The two must agree — the count they
// produce is what suppresses a no-op save in both renderers — and
// tests/preflight/check-survey-answer-delta.js holds both to one set of fixtures. Keep this file
// dependency-free; that gate evaluates it without a build and asserts the no-import property.
export const sameAnswer = (a, b) =>
    (a.code != null && a.code !== '' && b.code != null && b.code !== '')
        ? String(a.code) === String(b.code)
        : String(a.surveyQuestionId) === String(b.surveyQuestionId);

// Both halves of a delta come out of stored JSON, so neither is guaranteed to be a list. Anything
// that is not one is read as no answers, which the PHP twin does with the same is_array test. This is
// not defensive decoration: the answer count feeds a `renderable` predicate that the feed evaluates
// BEFORE its error boundary, so a single malformed row would take down the whole group rather than
// its own line.
const answerList = (value) => Array.isArray(value) ? value : [];

// Bool false is a real answer, not emptiness; so are numeric 0 and empty-looking dates.
// Only null/'' across every value column counts as no value.
const VALUE_FIELDS = ['valueText', 'valueNumeric', 'valueBool', 'valueDate'];
const surveyAnswerValueIsEmpty = (answer) =>
    VALUE_FIELDS.every((field) => answer?.[field] == null || answer[field] === '');

// Survey-answer activity deltas record changed/added answers in `newData` and the pre-change
// values in `oldData`. The answers a save cleared are those in `oldData` with no `newData`
// counterpart.
export const getRemovedSurveyAnswers = (oldData = [], newData = []) =>
    answerList(oldData).filter((removed) => !answerList(newData).some((kept) => sameAnswer(removed, kept)));

// A save that blanks a field (e.g. a text input cleared to "") keeps the answer in `newData`
// with empty value columns rather than omitting it. Surface those as cleared too — returning
// the `oldData` rows, which hold the value worth showing.
export const getClearedSurveyAnswers = (oldData = [], newData = []) =>
    answerList(oldData).filter((prior) => !surveyAnswerValueIsEmpty(prior)
        && answerList(newData).some((kept) => sameAnswer(prior, kept) && surveyAnswerValueIsEmpty(kept)));

// The changed and removed halves of an answer delta, as the answers pane displays them: a cleared
// answer counts once, as a removal carrying the prior value worth showing, and not a second time as
// a change. Both the pane and the activity description's answer count read this, so the sentence
// and the pane beneath it cannot disagree about how many answers a save touched.
export const partitionSurveyAnswerDelta = (oldData = [], newData = []) => {
    const cleared = getClearedSurveyAnswers(oldData, newData);

    return {
        changed: answerList(newData).filter((answer) => !cleared.some((prior) => sameAnswer(prior, answer))),
        removed: [...getRemovedSurveyAnswers(oldData, newData), ...cleared],
    };
};

// Resolve the survey question an answer belongs to, against question maps keyed by `code` and by
// surveyQuestionId (e.g. from useSurveyQuestions). Matches by `code` first, surveyQuestionId fallback.
export const resolveQuestionForAnswer = (answer, questionsByCode, questionsById) =>
    ((answer.code != null && answer.code !== '') ? questionsByCode[answer.code] : undefined)
        ?? questionsById[answer.surveyQuestionId];
